import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2, Upload, Receipt } from "lucide-react";
import { toast } from "sonner";
import { formatUSD, formatUSDCompact, formatDate } from "@/lib/format";
import DealExpenseDialog from "@/components/expenses/DealExpenseDialog";

const STAGES = [
  { key: "prospecting", label: "Prospecting" },
  { key: "active", label: "Active" },
  { key: "pending", label: "Pending" },
  { key: "closed", label: "Closed" },
  { key: "lost", label: "Lost" },
];

const SIDES = [
  { key: "listing", label: "Listing" },
  { key: "buyer", label: "Buyer" },
  { key: "both", label: "Both" },
  { key: "referral", label: "Referral" },
];

const FMLS_RATE = 0.0012; // 0.12% FMLS fee on sale price

const emptyForm = {
  property_name: "",
  address: "",
  client_name: "",
  side: "listing",
  stage: "prospecting",
  sale_price: 0,
  commission_rate: 3,
  broker_fee_flat: 0,
  fmls_fee: 0,
  fmls_fee_overridden: false,
  referral_fee: 0,
  transaction_fee: 0,
  close_date: "",
  notes: "",
};

function BreakdownCell({ label, value, muted = false, testid }) {
  return (
    <div className="border border-neutral-200 px-3 py-2" data-testid={testid}>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">{label}</div>
      <div className={`font-mono text-sm tracking-tight mt-1 ${muted ? "text-neutral-600" : "text-neutral-900"}`}>
        {formatUSD(value)}
      </div>
    </div>
  );
}

function DealCard({ deal, onEdit, onDelete, onExpenses, onDragStart }) {
  return (
    <div
      className="border border-neutral-200 bg-white p-3 mb-2 cursor-grab active:cursor-grabbing hover:border-neutral-900 transition-colors"
      draggable
      onDragStart={(e) => onDragStart(e, deal.id)}
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-serif text-base leading-tight truncate">{deal.property_name}</div>
        <div className="flex gap-1 shrink-0">
          <button
            onClick={() => onExpenses(deal)}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white text-neutral-700 font-mono text-[10px] uppercase tracking-[0.1em] transition-colors"
            title="Log expenses on this deal"
            data-testid={`deal-expenses-${deal.id}`}
          >
            <Receipt className="h-3 w-3" strokeWidth={1.75} />
            Expenses
          </button>
          <button onClick={() => onEdit(deal)} className="text-neutral-500 hover:text-neutral-900 px-1" title="Edit deal" data-testid={`edit-deal-${deal.id}`}>
            <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <button onClick={() => onDelete(deal.id)} className="text-neutral-500 hover:text-red-700 px-1" title="Delete deal" data-testid={`delete-deal-${deal.id}`}>
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
      {deal.client_name ? <div className="text-xs text-neutral-500 mt-1 truncate">{deal.client_name}</div> : null}
      <div className="mt-3 flex items-baseline justify-between border-t border-neutral-100 pt-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Gross</span>
        <span className="font-mono text-base">{formatUSD(deal.gross_commission)}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Net</span>
        <span className="font-mono text-sm" style={{ color: "var(--profit)" }}>{formatUSD(deal.net_commission)}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Sale</span>
        <span className="font-mono text-xs text-neutral-600">{formatUSD(deal.sale_price)}</span>
      </div>
      {deal.close_date ? <div className="font-mono text-[10px] text-neutral-400 mt-2">{formatDate(deal.close_date)}</div> : null}
    </div>
  );
}

export default function PipelinePage() {
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [expenseDeal, setExpenseDeal] = useState(null);
  const [scope, setScope] = useState("closed"); // "closed" | "pipeline"
  const fileInputRef = React.useRef(null);

  const load = useCallback(async () => {
    const res = await api.get("/deals");
    setDeals(res.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    // "closed" = stage=closed only (tax-report semantics; matches Dashboard KPI rail)
    // "pipeline" = every deal except lost (forecast semantics)
    const filtered = deals.filter((d) =>
      scope === "closed" ? d.stage === "closed" : d.stage !== "lost"
    );
    const sum = (key) => filtered.reduce((s, d) => s + Number(d[key] || 0), 0);
    return {
      closed_count: deals.filter((d) => d.stage === "closed").length,
      count: filtered.length,
      sales_volume: sum("sale_price"),
      gross_commission: sum("gross_commission"),
      broker_fees: sum("broker_fees"),
      net_commission: sum("net_commission"),
    };
  }, [deals, scope]);

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/deals/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const errs = res.data.errors?.length || 0;
      toast.success(`Imported ${res.data.imported} deals${errs ? ` (${errs} rows skipped)` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      e.target.value = "";
    }
  };

  const onDragStart = (e, id) => e.dataTransfer.setData("text/plain", id);
  const onDrop = async (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    if (!id) return;
    try {
      await api.patch(`/deals/${id}/stage`, { stage });
      toast.success(`Moved to ${stage}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d.id);
    setForm({
      ...emptyForm,
      ...d,
      close_date: d.close_date ? d.close_date.slice(0, 10) : "",
      // If server value differs from auto-derived, treat as manually overridden.
      fmls_fee_overridden:
        d.fmls_fee != null && Math.abs(Number(d.fmls_fee) - Number(d.sale_price || 0) * FMLS_RATE) > 0.01,
    });
    setOpen(true);
  };

  // Auto-derive FMLS fee from sale price unless the user has manually overridden it
  const onSalePriceChange = (raw) => {
    const nextSale = raw;
    setForm((f) => {
      const auto = Math.round((parseFloat(nextSale) || 0) * FMLS_RATE * 100) / 100;
      return {
        ...f,
        sale_price: nextSale,
        fmls_fee: f.fmls_fee_overridden ? f.fmls_fee : auto,
      };
    });
  };

  const onFmlsChange = (raw) => {
    setForm((f) => ({ ...f, fmls_fee: raw, fmls_fee_overridden: true }));
  };

  // Live breakdown — mirrors compute_deal_financials on the backend
  const breakdown = useMemo(() => {
    const sale = parseFloat(form.sale_price) || 0;
    const rate = parseFloat(form.commission_rate) || 0;
    const gross = sale * (rate / 100);
    const brokerFlat = parseFloat(form.broker_fee_flat) || 0;
    const fmls = parseFloat(form.fmls_fee) || 0;
    const referral = parseFloat(form.referral_fee) || 0;
    const txn = parseFloat(form.transaction_fee) || 0;
    const brokerFees = brokerFlat + fmls;
    const net = gross - brokerFees - referral - txn;
    return { gross, brokerFlat, fmls, referral, txn, net };
  }, [form.sale_price, form.commission_rate, form.broker_fee_flat, form.fmls_fee, form.referral_fee, form.transaction_fee]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      sale_price: parseFloat(form.sale_price) || 0,
      commission_rate: parseFloat(form.commission_rate) || 0,
      broker_fee_flat: parseFloat(form.broker_fee_flat) || 0,
      fmls_fee: parseFloat(form.fmls_fee) || 0,
      referral_fee: parseFloat(form.referral_fee) || 0,
      transaction_fee: parseFloat(form.transaction_fee) || 0,
      close_date: form.close_date || null,
    };
    // Strip UI-only bookkeeping + legacy fields before hitting the API
    delete payload.fmls_fee_overridden;
    delete payload.broker_split_pct;
    try {
      if (editing) {
        await api.put(`/deals/${editing}`, payload);
        toast.success("Deal updated");
      } else {
        await api.post(`/deals`, payload);
        toast.success("Deal created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    try {
      await api.delete(`/deals/${id}`);
      toast.success("Deal deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="px-6 lg:px-10 py-8" data-testid="pipeline-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Workflow</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Deal Pipeline</h1>
          <p className="text-sm text-neutral-500 mt-2">Drag cards between columns. Closing a deal rolls it into your tax report.</p>
        </div>
        <div className="flex gap-2">
        <Button
          onClick={openCreate}
          className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-10"
          data-testid="pipeline-add-deal-button"
        >
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          New deal
        </Button>
        <Button
          onClick={() => fileInputRef.current?.click()}
          variant="outline"
          className="rounded-none border-neutral-300 hover:bg-neutral-100 h-10"
          data-testid="pipeline-import-csv-button"
        >
          <Upload className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Import CSV
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleCsvImport}
          data-testid="pipeline-csv-file-input"
        />
        </div>
      </div>

      {/* KPI summary band — mirrors Dashboard KPI rail, filtered by scope */}
      <div className="border border-neutral-200 mb-6" data-testid="pipeline-kpi-band">
        <div className="flex items-center justify-between px-6 py-3 border-b border-neutral-200 bg-neutral-50/40">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            {scope === "closed" ? "Year-to-Date · Closed Deals" : "Pipeline · All Active"}
          </div>
          <div className="flex" data-testid="pipeline-kpi-scope-toggle">
            {[
              ["closed", "Closed Only"],
              ["pipeline", "Full Pipeline"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setScope(k)}
                className={`px-3 py-1 text-xs font-mono border border-r-0 last:border-r border-neutral-300 transition-colors ${
                  scope === k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
                data-testid={`pipeline-kpi-scope-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5">
          {[
            ["Closed Deals", scope === "closed" ? totals.count : totals.closed_count, "kpi-closed-deals"],
            ["Sales Volume", formatUSDCompact(totals.sales_volume), "kpi-sales-volume"],
            ["Gross Commission", formatUSDCompact(totals.gross_commission), "kpi-gross-commission"],
            ["Broker Fees", formatUSDCompact(totals.broker_fees), "kpi-broker-fees"],
            ["Net Commission", formatUSDCompact(totals.net_commission), "kpi-net-commission"],
          ].map(([label, value, testid], i) => (
            <div
              key={label}
              className={`px-6 py-4 border-r border-neutral-200 last:border-r-0 ${i >= 2 ? "border-t md:border-t-0" : ""}`}
              data-testid={testid}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1.5">{label}</div>
              <div className="font-mono text-2xl tracking-tight text-neutral-900">{value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 border border-neutral-200">
        {STAGES.map((s) => {
          const items = deals.filter((d) => d.stage === s.key);
          const total = items.reduce((sum, d) => sum + Number(d.gross_commission || 0), 0);
          return (
            <div
              key={s.key}
              className="pipe-col p-4 min-h-[60vh] bg-neutral-50/40"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(e, s.key)}
              data-testid={`pipeline-column-${s.key}`}
            >
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-neutral-200">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-700">{s.label}</span>
                <span className="font-mono text-xs text-neutral-500">{items.length}</span>
              </div>
              <div className="font-mono text-xs text-neutral-500 mb-3">{formatUSD(total)}</div>
              {items.map((d) => (
                <DealCard
                  key={d.id}
                  deal={d}
                  onEdit={openEdit}
                  onDelete={remove}
                  onExpenses={setExpenseDeal}
                  onDragStart={onDragStart}
                />
              ))}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl border-neutral-300" data-testid="deal-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit deal" : "New deal"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Property / Deal name</Label>
              <Input required value={form.property_name} onChange={(e) => setForm({ ...form, property_name: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="deal-property-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Address</Label>
              <Input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="deal-address-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Client</Label>
              <Input value={form.client_name || ""} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="deal-client-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Side</Label>
              <Select value={form.side} onValueChange={(v) => setForm({ ...form, side: v })}>
                <SelectTrigger className="mt-2 rounded-none border-neutral-300 focus:ring-0" data-testid="deal-side-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {SIDES.map((s) => <SelectItem key={s.key} value={s.key} className="rounded-none">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                <SelectTrigger className="mt-2 rounded-none border-neutral-300 focus:ring-0" data-testid="deal-stage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {STAGES.map((s) => <SelectItem key={s.key} value={s.key} className="rounded-none">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Close date</Label>
              <Input type="date" value={form.close_date || ""} onChange={(e) => setForm({ ...form, close_date: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="deal-close-date-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Sale price</Label>
              <Input type="number" step="0.01" value={form.sale_price} onChange={(e) => onSalePriceChange(e.target.value)}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono" data-testid="deal-sale-price-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Commission %</Label>
              <Input type="number" step="0.01" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono" data-testid="deal-commission-rate-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500 flex items-center justify-between">
                <span>FMLS 0.12% Fee</span>
                {form.fmls_fee_overridden ? (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({
                      ...f,
                      fmls_fee: Math.round((parseFloat(f.sale_price) || 0) * FMLS_RATE * 100) / 100,
                      fmls_fee_overridden: false,
                    }))}
                    className="text-[9px] font-mono uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 underline underline-offset-2"
                    data-testid="deal-fmls-reset"
                  >
                    Auto
                  </button>
                ) : null}
              </Label>
              <Input
                type="number"
                step="0.01"
                value={form.fmls_fee}
                onChange={(e) => onFmlsChange(e.target.value)}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono"
                data-testid="deal-fmls-fee-input"
              />
              <div className="mt-1 font-mono text-[10px] text-neutral-500">
                {form.fmls_fee_overridden ? "Manually overridden" : "Auto-calculated from sale price × 0.12%"}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Broker flat fee</Label>
              <Input type="number" step="0.01" value={form.broker_fee_flat} onChange={(e) => setForm({ ...form, broker_fee_flat: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono" data-testid="deal-broker-flat-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Referral fee</Label>
              <Input type="number" step="0.01" value={form.referral_fee} onChange={(e) => setForm({ ...form, referral_fee: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono" data-testid="deal-referral-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Transaction fee</Label>
              <Input type="number" step="0.01" value={form.transaction_fee} onChange={(e) => setForm({ ...form, transaction_fee: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900 font-mono" data-testid="deal-txn-fee-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">Notes</Label>
              <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900" data-testid="deal-notes-input" />
            </div>

            {/* Live breakdown — mirrors server-side compute_deal_financials */}
            <div
              className="col-span-2 border-t border-neutral-200 pt-4 mt-1"
              data-testid="deal-live-breakdown"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">
                Live breakdown
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <BreakdownCell label="Gross" value={breakdown.gross} testid="breakdown-gross" />
                <BreakdownCell label="FMLS 0.12%" value={breakdown.fmls} muted testid="breakdown-fmls" />
                <BreakdownCell label="Broker flat" value={breakdown.brokerFlat} muted testid="breakdown-broker-flat" />
                <BreakdownCell
                  label="Referral + Txn"
                  value={breakdown.referral + breakdown.txn}
                  muted
                  testid="breakdown-other"
                />
              </div>
              <div className="mt-3 flex items-baseline justify-between border-t border-neutral-100 pt-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500">Net Commission</span>
                <span
                  className="font-mono text-2xl tracking-tight"
                  style={{ color: breakdown.net >= 0 ? "var(--profit, #16a34a)" : "var(--expense, #b91c1c)" }}
                  data-testid="breakdown-net"
                >
                  {formatUSD(breakdown.net)}
                </span>
              </div>
            </div>

            <DialogFooter className="col-span-2 mt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}
                className="rounded-none border-neutral-300 hover:bg-neutral-100" data-testid="deal-cancel-button">
                Cancel
              </Button>
              <Button type="submit" className="rounded-none bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="deal-submit-button">
                {editing ? "Save changes" : "Create deal"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {expenseDeal && (
        <DealExpenseDialog deal={expenseDeal} onClose={() => setExpenseDeal(null)} />
      )}
    </div>
  );
}
